import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import cn from 'classnames';

import './TokenizedInput.scss';

const COMMA_KEY_CODE = 188;
const ENTER_KEY_CODE = 13;

function TokenizedInput({
  id,
  className,
  isSaving = false,
  maxInputLength = 100,
  handleInputChange: onInputChange,
  tokens,
  getTokenKey,
  renderToken,
  renderNewToken,
  handleTokenSave,
  ariaDescribedBy
}) {
  const [focused, setFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [newTokenValue, setNewTokenValue] = useState('');
  const prevIsSavingRef = useRef(isSaving);
  const prevTokensRef = useRef(tokens);
  const componentRef = useRef(null);
  const inputRef = useRef(null);

  // Replicate getDerivedStateFromProps: when isSaving transitions true→false
  // and there was a pending newTokenValue, resolve it
  useEffect(() => {
    if (newTokenValue && prevIsSavingRef.current && !isSaving) {
      const tokenCountUnchanged =
        prevTokensRef.current &&
        (prevTokensRef.current.length || prevTokensRef.current.size) === (tokens.length || tokens.size);

      if (tokenCountUnchanged) {
        setInputValue(newTokenValue);
      } else {
        setInputValue('');
      }
      setNewTokenValue('');
    }
    prevIsSavingRef.current = isSaving;
    prevTokensRef.current = tokens;
  }, [isSaving, tokens, newTokenValue]);

  // Click anywhere on the component to focus the input
  useEffect(() => {
    const el = componentRef.current;
    if (!el) return undefined;

    function handleComponentClick() {
      if (inputRef.current && el !== document.activeElement && !el.contains(document.activeElement)) {
        inputRef.current.focus();
      }
    }

    el.addEventListener('click', handleComponentClick);
    return () => el.removeEventListener('click', handleComponentClick);
  }, []);

  const handleInputFocus = useCallback(() => setFocused(true), []);
  const handleInputBlur = useCallback(() => setFocused(false), []);

  const handleInputChange = useCallback(
    e => {
      setInputValue(e.target.value);
      if (onInputChange) {
        onInputChange(e.target.value);
      }
    },
    [onInputChange]
  );

  const handleInputKeyDown = useCallback(
    e => {
      if (newTokenValue || isSaving) {
        e.preventDefault();
        return;
      }

      if ([COMMA_KEY_CODE, ENTER_KEY_CODE].indexOf(e.keyCode) !== -1) {
        e.preventDefault();

        const trimmed = inputValue.trim();
        if (trimmed) {
          setNewTokenValue(trimmed);
          setInputValue('');
          handleTokenSave(trimmed);
        }
      }
    },
    [newTokenValue, isSaving, inputValue, handleTokenSave]
  );

  function renderTokensList() {
    const renderedTokens = tokens.map(token => (
      <li key={getTokenKey(token)} className="token-item">
        {renderToken(token)}
      </li>
    ));

    if (newTokenValue) {
      renderedTokens.push(
        <li key="new-token-item" className="token-item">
          {renderNewToken(newTokenValue)}
        </li>
      );
    }

    if (renderedTokens.length || renderedTokens.size) {
      return <ul className="tokens-list">{renderedTokens}</ul>;
    }

    return null;
  }

  return (
    <div className={cn('cp-tokenized-input', { focused }, className)} ref={componentRef}>
      {renderTokensList()}

      <span className="new-token-input">
        <input
          id={id}
          type="text"
          autoComplete="off"
          autoCorrect="off"
          className="new-token-text-field"
          maxLength={maxInputLength}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          value={inputValue}
          ref={inputRef}
          aria-describedby={ariaDescribedBy}
        />
        <span className="input-resizer" aria-hidden>
          {inputValue}
          &nbsp;
        </span>
      </span>
    </div>
  );
}

TokenizedInput.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  isSaving: PropTypes.bool,
  maxInputLength: PropTypes.number,
  handleInputChange: PropTypes.func,
  tokens: PropTypes.oneOfType([PropTypes.array, ImmutablePropTypes.list]).isRequired,
  getTokenKey: PropTypes.func.isRequired,
  renderToken: PropTypes.func.isRequired,
  renderNewToken: PropTypes.func.isRequired,
  handleTokenSave: PropTypes.func.isRequired,
  ariaDescribedBy: PropTypes.string
};

export default TokenizedInput;
